import React, { useEffect, useState } from 'react';
import { ProductApi } from 'common/types';
import {
	Typography,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	CircularProgress,
} from '@mui/material';
import { Theme } from '@mui/material/styles';

import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';

import { moveItem, getOrderIndexesToUpdate } from 'common/utils/arrays';
import ProductList from 'components/ProductList';

import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'services/localization';
import * as StockSelectors from 'selectors/StockSelectors';
import * as NotificationActions from 'actions/NotificationActions';
import { api } from 'common/frontend/api';
import { BatchManager } from 'common/frontend/firebase/firestore';

interface Props {
	open: boolean;
	onClose: () => void;
	products: ProductApi[];
}
const ProductReorderDialog = (props: Props) => {
	const classes = useStyles();
	const dispatch = useDispatch();
	const { t } = useTranslation();
	const { open, onClose, products } = props;

	const productIdsToShow = products.map((p) => p.id);

	const allProducts = useSelector(StockSelectors.stockProductsData);
	const [loading, setLoading] = useState<boolean>(false);
	const [reorderedProducts, setReorderedProducts] = useState<ProductApi[]>(allProducts);
	const productsToShow = reorderedProducts.filter((p) => productIdsToShow.includes(p.id));

	useEffect(() => {
		if (!open) {
			setReorderedProducts(allProducts);
		}
	}, [allProducts, open]);

	const handleClose = () => {
		setReorderedProducts(allProducts);
		onClose();
	};

	const updateProductOrderIndexes = async (
		products: (ProductApi & {
			orderIndex: number;
		})[],
	) => {
		const batchManager = new BatchManager();
		products.forEach((product) => {
			api(batchManager.batch()).products.doc(product.id).update({
				orderIndex: product.orderIndex,
			});
		});
		await batchManager.commit();
	};

	const handleSave = async () => {
		setLoading(true);
		try {
			const productsToUpdate = getOrderIndexesToUpdate(reorderedProducts);
			if (productsToUpdate.length > 0) {
				await updateProductOrderIndexes(productsToUpdate);
			}
			handleClose();
		} catch (err) {
			dispatch(
				NotificationActions.showNotification({
					message: t('common:errors.somethingWentWrong'),
					variant: 'error',
				}),
			);
		}
		setLoading(false);
	};

	const handleMoveUp = (productId: string, currentIndex: number) => {
		const prevItem = productsToShow[currentIndex - 1];
		if (!prevItem) return;

		const itemIndex = reorderedProducts.findIndex((item) => item.id === productId);
		const targetIndex = reorderedProducts.findIndex((item) => item.id === prevItem.id);

		if (itemIndex === -1 || targetIndex === -1) return;

		const result = moveItem(reorderedProducts, itemIndex, targetIndex, 'before');
		setReorderedProducts(result);
	};

	const handleMoveDown = (productId: string, currentIndex: number) => {
		const nextItem = productsToShow[currentIndex + 1];
		if (!nextItem) return;
		const itemIndex = reorderedProducts.findIndex((item) => item.id === productId);
		const targetIndex = reorderedProducts.findIndex((item) => item.id === nextItem.id);
		if (itemIndex === -1 || targetIndex === -1) return;

		const result = moveItem(reorderedProducts, itemIndex, targetIndex, 'after');
		setReorderedProducts(result);
	};

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
			<DialogTitle>
				<Typography variant="h6" component="div">
					{t('products.reorderProducts', 'Reorder products')}
				</Typography>
				<Typography variant="body2" component="div">
					{t(
						'products.reorderProductsDescription',
						'Change the order in which your products are displayed',
					)}
				</Typography>
			</DialogTitle>
			<DialogContent className={classes.content}>
				<ProductList
					products={productsToShow}
					onMoveUp={handleMoveUp}
					onMoveDown={handleMoveDown}
				/>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose}>{'Cancel'}</Button>
				<Button onClick={handleSave} color="primary" variant="contained">
					{loading ? <CircularProgress size={20} color="inherit" /> : t('common:actions.save')}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		content: {
			minHeight: 200,
		},
	}),
);

export default ProductReorderDialog;
